/** Pure inbound after-sales contracts. No provider I/O. */

import { ebayInboundCancelIsActionableBuyer } from "./inbound-buyer-cancel";

export type EbayInboundRequestKind =
  | "buyer_cancellation"
  | "buyer_return"
  | "buyer_inr"
  | "buyer_snad"
  | "ebay_case"
  | "payment_dispute";

export const EBAY_INBOUND_REQUEST_KINDS: readonly EbayInboundRequestKind[] = [
  "buyer_cancellation",
  "buyer_return",
  "buyer_inr",
  "buyer_snad",
  "ebay_case",
  "payment_dispute",
];

export const EBAY_PAYMENT_DISPUTE_SCOPE = "https://api.ebay.com/oauth/api_scope/sell.payment.dispute";
export const EBAY_INBOUND_OAUTH_SCOPES_PRESENT = [
  "https://api.ebay.com/oauth/api_scope/sell.inventory",
  "https://api.ebay.com/oauth/api_scope/sell.account",
  "https://api.ebay.com/oauth/api_scope/sell.fulfillment",
  "https://api.ebay.com/oauth/api_scope/commerce.identity.readonly",
] as const;

const CLOSED_STATES = new Set([
  "CLOSED",
  "COMPLETED",
  "CANCELLED",
  "CANCELED",
  "EXPIRED",
  "DECLINED",
  "CLOSED_WITH_REFUND",
  "CLOSED_NO_REFUND",
  "RETURN_CLOSED",
  "CS_CLOSED",
  "CLOSED_SUCCESS",
  "CLOSED_FAILURE",
  "WON",
  "LOST",
  "RESOLVED",
  "CANCEL_CLOSED_WITH_REFUND",
  "CANCEL_CLOSED_NO_REFUND",
  "CANCEL_CLOSED",
]);

const ACTIONABLE_STATES = new Set([
  "OPEN",
  "PENDING",
  "REQUESTED",
  "IN_PROGRESS",
  "WAITING_SELLER",
  "WAITING_SELLER_RESPONSE",
  "SELLER_ACTION_DUE",
  "ACTION_NEEDED",
  "SELLER_RESPONSE_NEEDED",
  "ON_HOLD",
  "WAITING_CS",
  "WAITING_BUYER",
  "WAITING_BUYER_RESPONSE",
  "RETURN_REQUESTED",
  "ITEM_READY_TO_SHIP",
  "PARTIAL",
  "ESCALATED",
  "APPROVE",
]);

const SNAD_REASONS = new Set([
  "NOT_AS_DESCRIBED",
  "SIGNIFICANTLY_NOT_AS_DESCRIBED",
  "SNAD",
  "INAD",
  "DEFECTIVE_ITEM",
  "DOES_NOT_WORK_OR_DEFECTIVE",
  "WRONG_ITEM",
  "MISSING_PARTS",
  "FAKE_OR_COUNTERFEIT",
  "ARRIVED_DAMAGED",
  "DOES_NOT_MATCH_DESCRIPTION",
]);

const INR_REASONS = new Set([
  "ITEM_NOT_RECEIVED",
  "INR",
  "NOT_RECEIVED",
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function asText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  const rec = asRecord(value);
  if (rec && "value" in rec) return asText(rec.value);
  return "";
}

function upper(value: unknown): string {
  return asText(value).toUpperCase();
}

function boundOrderPath(boundExternalOrderId: unknown, requestedExternalOrderId: unknown, unboundCode: string, mismatchCode: string):
  | { ok: true; bound: string }
  | { ok: false; code: string; message: string } {
  const bound = asText(boundExternalOrderId);
  if (!bound || /[?&/]/.test(bound)) {
    return { ok: false, code: unboundCode, message: "Inbound search requires a bound eBay order id." };
  }
  const requested = asText(requestedExternalOrderId) || bound;
  if (requested !== bound) {
    return { ok: false, code: mismatchCode, message: "Inbound search may only target the bound remote order id." };
  }
  return { ok: true, bound };
}

export function ebayInboundReasonIsSnad(reason: unknown): boolean {
  const raw = upper(reason);
  return SNAD_REASONS.has(raw) || raw.includes("NOT_AS_DESCRIBED") || raw.includes("SNAD") || raw.includes("INAD");
}

export function ebayInboundReasonIsInr(reason: unknown): boolean {
  const raw = upper(reason);
  return INR_REASONS.has(raw) || raw.includes("NOT_RECEIVED") || raw.includes("INR");
}

export function ebayInboundStateIsClosed(state: unknown): boolean {
  return CLOSED_STATES.has(upper(state));
}

export function ebayInboundStateIsActionable(state: unknown): boolean {
  if (ebayInboundStateIsClosed(state)) return false;
  const raw = upper(state);
  return ACTIONABLE_STATES.has(raw) || (raw.includes("SELLER") && raw.includes("WAIT")) || raw.includes("OPEN");
}

export function ebayInboundInitiatorIsBuyer(initiator: unknown): boolean {
  const raw = upper(initiator);
  if (!raw) return true;
  return raw === "BUYER" || raw === "CUSTOMER" || raw === "PAYPAL" || raw === "EBAY";
}

export function ebayInboundKindFromReturn(opts: { reason?: unknown; reasonType?: unknown }): EbayInboundRequestKind {
  return ebayInboundReasonIsSnad(opts.reasonType ?? opts.reason) ? "buyer_snad" : "buyer_return";
}

export function ebayInboundEventIsActionable(opts: {
  kind: EbayInboundRequestKind;
  initiator?: unknown;
  requestState?: unknown;
  orderCancelState?: unknown;
}): boolean {
  if (opts.kind === "buyer_cancellation") {
    return ebayInboundCancelIsActionableBuyer({
      initiator: opts.initiator,
      requestState: opts.requestState,
      orderCancelState: opts.orderCancelState,
    });
  }
  if (!ebayInboundInitiatorIsBuyer(opts.initiator) && (opts.kind === "buyer_return" || opts.kind === "buyer_snad" || opts.kind === "buyer_inr")) {
    return false;
  }
  return ebayInboundStateIsActionable(opts.requestState);
}

export function ebayInboundIssueSpec(kind: EbayInboundRequestKind): {
  exceptionType: string;
  notificationType: string;
  title: string;
  entityType: string;
} {
  switch (kind) {
    case "buyer_cancellation":
      return { exceptionType: "buyer_cancellation_requested", notificationType: "buyer_cancellation_requested", title: "Buyer requested cancellation", entityType: "provider_cancellation" };
    case "buyer_return":
      return { exceptionType: "buyer_return_opened", notificationType: "buyer_return_opened", title: "Buyer opened a return", entityType: "provider_return" };
    case "buyer_inr":
      return { exceptionType: "buyer_item_not_received", notificationType: "buyer_item_not_received", title: "Buyer reports item not received", entityType: "provider_inquiry" };
    case "buyer_snad":
      return { exceptionType: "buyer_item_not_as_described", notificationType: "buyer_item_not_as_described", title: "Buyer reports item not as described", entityType: "provider_return" };
    case "ebay_case":
      return { exceptionType: "ebay_case_requires_attention", notificationType: "ebay_case_requires_attention", title: "eBay case requires attention", entityType: "provider_case" };
    case "payment_dispute":
      return { exceptionType: "payment_dispute_requires_attention", notificationType: "payment_dispute_requires_attention", title: "Payment dispute requires attention", entityType: "provider_payment_dispute" };
  }
}

export function ebayInboundBindKey(opts: {
  connectorId: unknown;
  requestKind: unknown;
  providerRequestId: unknown;
}): string {
  return `${asText(opts.connectorId)}::${asText(opts.requestKind)}::${asText(opts.providerRequestId)}`;
}

export type EbayInboundEventEvidence = {
  providerRequestId: string;
  kind: EbayInboundRequestKind;
  legacyOrderId: string;
  initiator: string;
  reason: string;
  requestState: string;
  requestedAt: string | null;
};

function dateValue(raw: unknown): string | null {
  return asText(asRecord(raw)?.value ?? raw) || null;
}

export function planEbayInboundReturnSearch(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
}): { ok: true; method: "GET"; path: string; externalOrderId: string } | { ok: false; code: string; message: string } {
  const bound = boundOrderPath(opts.boundExternalOrderId, opts.requestedExternalOrderId, "RETURN_SEARCH_UNBOUND", "RETURN_SEARCH_ID_MISMATCH");
  if (bound.ok === false) return { ok: false, code: bound.code, message: bound.message };
  return {
    ok: true,
    method: "GET",
    path: `/post-order/v2/return/search?order_id=${encodeURIComponent(bound.bound)}`,
    externalOrderId: bound.bound,
  };
}

export function planEbayInboundReturnGet(opts: {
  returnId?: unknown;
}): { ok: true; method: "GET"; path: string; returnId: string } | { ok: false; code: string; message: string } {
  const id = asText(opts.returnId);
  if (!id || /[?&/]/.test(id)) {
    return { ok: false, code: "RETURN_GET_UNBOUND", message: "Return GET requires a provider return id." };
  }
  return { ok: true, method: "GET", path: `/post-order/v2/return/${encodeURIComponent(id)}`, returnId: id };
}

export function planEbayInquirySearch(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
}): { ok: true; method: "GET"; path: string; externalOrderId: string } | { ok: false; code: string; message: string } {
  const bound = boundOrderPath(opts.boundExternalOrderId, opts.requestedExternalOrderId, "INQUIRY_SEARCH_UNBOUND", "INQUIRY_SEARCH_ID_MISMATCH");
  if (bound.ok === false) return { ok: false, code: bound.code, message: bound.message };
  return {
    ok: true,
    method: "GET",
    path: `/post-order/v2/inquiry/search?order_id=${encodeURIComponent(bound.bound)}`,
    externalOrderId: bound.bound,
  };
}

export function planEbayInquiryGet(opts: {
  inquiryId?: unknown;
}): { ok: true; method: "GET"; path: string; inquiryId: string } | { ok: false; code: string; message: string } {
  const id = asText(opts.inquiryId);
  if (!id || /[?&/]/.test(id)) {
    return { ok: false, code: "INQUIRY_GET_UNBOUND", message: "Inquiry GET requires a provider inquiry id." };
  }
  return { ok: true, method: "GET", path: `/post-order/v2/inquiry/${encodeURIComponent(id)}`, inquiryId: id };
}

export function planEbayCaseSearch(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
}): { ok: true; method: "GET"; path: string; externalOrderId: string } | { ok: false; code: string; message: string } {
  const bound = boundOrderPath(opts.boundExternalOrderId, opts.requestedExternalOrderId, "CASE_SEARCH_UNBOUND", "CASE_SEARCH_ID_MISMATCH");
  if (bound.ok === false) return { ok: false, code: bound.code, message: bound.message };
  return {
    ok: true,
    method: "GET",
    path: `/post-order/v2/casemanagement/search?order_id=${encodeURIComponent(bound.bound)}`,
    externalOrderId: bound.bound,
  };
}

export function planEbayCaseGet(opts: {
  caseId?: unknown;
}): { ok: true; method: "GET"; path: string; caseId: string } | { ok: false; code: string; message: string } {
  const id = asText(opts.caseId);
  if (!id || /[?&/]/.test(id)) {
    return { ok: false, code: "CASE_GET_UNBOUND", message: "Case GET requires a provider case id." };
  }
  return { ok: true, method: "GET", path: `/post-order/v2/casemanagement/${encodeURIComponent(id)}`, caseId: id };
}

export function planEbayPaymentDisputeSummary(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
}): { ok: true; method: "GET"; path: string; externalOrderId: string; requiredScope: string } | { ok: false; code: string; message: string } {
  const bound = boundOrderPath(opts.boundExternalOrderId, opts.requestedExternalOrderId, "PAYMENT_DISPUTE_SEARCH_UNBOUND", "PAYMENT_DISPUTE_SEARCH_ID_MISMATCH");
  if (bound.ok === false) return { ok: false, code: bound.code, message: bound.message };
  return {
    ok: true,
    method: "GET",
    path: `/sell/fulfillment/v1/payment_dispute_summary?order_id=${encodeURIComponent(bound.bound)}`,
    externalOrderId: bound.bound,
    requiredScope: EBAY_PAYMENT_DISPUTE_SCOPE,
  };
}

export function classifyInboundGetAuth(httpStatus: unknown): "ok" | "blocked" | "failed" {
  const n = Number(httpStatus);
  if (n === 401 || n === 403) return "blocked";
  if (Number.isFinite(n) && n >= 200 && n < 300) return "ok";
  return "failed";
}

export type InboundFamilyPollOutcome = "checked_empty" | "discovered" | "blocked" | "failed";
export type InboundFamilyErrorClass = "AUTH_SCOPE" | "INVALID_CONTRACT" | "PROVIDER_ERROR" | "NETWORK" | null;

export const INBOUND_FAMILY_POLL_FAMILIES = ["cancellation", "return", "inr", "case", "payment_dispute"] as const;
export type InboundFamilyPollFamily = (typeof INBOUND_FAMILY_POLL_FAMILIES)[number];

export function classifyInboundFamilyPoll(opts: {
  httpStatus?: unknown;
  eventCount?: unknown;
  networkError?: boolean;
  planOk?: boolean;
}): {
  outcome: InboundFamilyPollOutcome;
  errorClass: InboundFamilyErrorClass;
  createBuyerIssue: false;
  persistSecrets: false;
  mutateProvider: false;
} {
  const count = Math.max(0, Math.floor(Number(opts.eventCount) || 0));
  if (opts.planOk === false) {
    return { outcome: "failed", errorClass: "INVALID_CONTRACT", createBuyerIssue: false, persistSecrets: false, mutateProvider: false };
  }
  if (opts.networkError) {
    return { outcome: "failed", errorClass: "NETWORK", createBuyerIssue: false, persistSecrets: false, mutateProvider: false };
  }
  const auth = classifyInboundGetAuth(opts.httpStatus);
  if (auth === "blocked") {
    return { outcome: "blocked", errorClass: "AUTH_SCOPE", createBuyerIssue: false, persistSecrets: false, mutateProvider: false };
  }
  const n = Number(opts.httpStatus);
  if (n === 400 || n === 404) {
    return { outcome: "failed", errorClass: "INVALID_CONTRACT", createBuyerIssue: false, persistSecrets: false, mutateProvider: false };
  }
  if (auth === "ok") {
    return {
      outcome: count > 0 ? "discovered" : "checked_empty",
      errorClass: null,
      createBuyerIssue: false,
      persistSecrets: false,
      mutateProvider: false,
    };
  }
  return { outcome: "failed", errorClass: "PROVIDER_ERROR", createBuyerIssue: false, persistSecrets: false, mutateProvider: false };
}

export function inboundFamilyPollMustNotCreateBuyerIssue(outcome: InboundFamilyPollOutcome): boolean {
  return outcome === "checked_empty" || outcome === "blocked" || outcome === "failed";
}

export function inboundFamilyHealthExceptionType(): "inbound_family_poll_blocked" {
  return "inbound_family_poll_blocked";
}

/**
 * Normalize eBay OAuth scope lists for exact-URI matching.
 * Accepts jsonb arrays, space/comma-separated strings, or null.
 * Missing/empty metadata yields []. Short names such as "sell.inventory"
 * are kept as-is and do not match full scope URIs.
 *
 * IMPORTANT: values persisted on connector_ebay_config.scopes are REQUESTED
 * authorize metadata, not trusted provider-granted authority.
 */
export function normalizeEbayGrantedScopes(raw: unknown): string[] {
  if (raw == null) return [];
  let parts: string[] = [];
  if (Array.isArray(raw)) {
    parts = raw.map((s) => String(s ?? "").trim()).filter(Boolean);
  } else if (typeof raw === "string") {
    parts = raw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  } else {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const scope of parts) {
    if (seen.has(scope)) continue;
    seen.add(scope);
    out.push(scope);
  }
  return out;
}

/** Exact scope membership — no substring / suffix matching. */
export function ebayGrantedScopesInclude(grantedScopes: unknown, requiredScope: string): boolean {
  const required = String(requiredScope ?? "").trim();
  if (!required) return false;
  return normalizeEbayGrantedScopes(grantedScopes).includes(required);
}

/**
 * Resolve REQUESTED authorize scopes for OAuth callback persistence.
 * Uses the exact scopes bound to the verified OAuth state for that connector.
 * Optional token `scope` is returned separately as an untrusted claim only —
 * eBay user-token responses often omit scope, and even when present it is not
 * treated as forge-proof granted authority (no documented introspection API).
 */
export function resolveEbayOAuthRequestedScopes(opts: {
  stateRequestedScopes?: unknown;
}): string[] {
  return normalizeEbayGrantedScopes(opts.stateRequestedScopes);
}

/** @deprecated Use resolveEbayOAuthRequestedScopes — scopes are requested metadata. */
export function resolveEbayOAuthGrantedScopes(opts: {
  tokenScope?: unknown;
  stateRequestedScopes?: unknown;
}): string[] {
  return resolveEbayOAuthRequestedScopes({
    stateRequestedScopes: opts.stateRequestedScopes,
  });
}

/** Trusted payment-dispute capability evidence (provider-proven, not requested). */
export type EbayPaymentDisputeCapabilityStatus = "unverified" | "verified" | "denied";

export function readEbayPaymentDisputeCapabilityStatus(
  capabilitiesSnapshot: unknown,
): EbayPaymentDisputeCapabilityStatus {
  if (!capabilitiesSnapshot || typeof capabilitiesSnapshot !== "object" || Array.isArray(capabilitiesSnapshot)) {
    return "unverified";
  }
  const inbound = (capabilitiesSnapshot as Record<string, unknown>).inbound_family_poll;
  if (!inbound || typeof inbound !== "object" || Array.isArray(inbound)) return "unverified";
  const payment = (inbound as Record<string, unknown>).payment_dispute;
  if (!payment || typeof payment !== "object" || Array.isArray(payment)) return "unverified";
  const status = String((payment as Record<string, unknown>).status ?? "").trim().toLowerCase();
  if (status === "verified" || status === "denied") return status;
  return "unverified";
}

/** Opaque credential-generation id from connector_ebay_config (null = legacy). */
export function normalizeEbayCredentialGeneration(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)) {
    return null;
  }
  return v.toLowerCase();
}

/**
 * Map a provider HTTP outcome onto trusted capability status.
 * 2xx establishes verified; 401/403 establishes denied; other outcomes leave
 * status unchanged (caller should not rewrite authority).
 */
export function ebayPaymentDisputeCapabilityFromProviderHttp(
  httpStatus: number | null | undefined,
): EbayPaymentDisputeCapabilityStatus | null {
  if (httpStatus == null || !Number.isFinite(httpStatus)) return null;
  if (httpStatus >= 200 && httpStatus < 300) return "verified";
  if (httpStatus === 401 || httpStatus === 403) return "denied";
  return null;
}

/**
 * Capability gate for family HTTP polls.
 *
 * Trusted authority is capabilities_snapshot payment_dispute status only.
 * Requested scopes never grant trust; they only permit a controlled probe
 * attempt while status is unverified (so evidence can be established).
 * Legacy connectors without requested dispute intent fail closed with no HTTP.
 */
export function ebayInboundFamilyPollCapability(opts: {
  family: InboundFamilyPollFamily;
  capabilityStatus?: EbayPaymentDisputeCapabilityStatus | null;
  /** Opaque OAuth credential generation; NULL/missing fails closed (no probe). */
  credentialGeneration?: string | null;
  /** Requested authorize scopes — probe gate only, never trusted grant. */
  requestedScopes?: unknown;
  /** @deprecated Ignored for trust; use capabilityStatus + requestedScopes. */
  grantedScopes?: unknown;
}):
  | { poll: true; mode: "poll" | "probe"; reason: null }
  | { poll: false; outcome: "blocked"; errorClass: "AUTH_SCOPE"; reason: string; httpStatus: null } {
  if (opts.family !== "payment_dispute") {
    return { poll: true, mode: "poll", reason: null };
  }
  // Legacy NULL generation cannot establish or exercise dispute capability.
  if (!normalizeEbayCredentialGeneration(opts.credentialGeneration)) {
    return {
      poll: false,
      outcome: "blocked",
      errorClass: "AUTH_SCOPE",
      reason: "eBay payment dispute capability requires a credential generation; reauthorize this connector",
      httpStatus: null,
    };
  }
  const status = opts.capabilityStatus ?? "unverified";
  if (status === "verified") {
    return { poll: true, mode: "poll", reason: null };
  }
  if (status === "denied") {
    return {
      poll: false,
      outcome: "blocked",
      errorClass: "AUTH_SCOPE",
      reason: "eBay payment dispute capability is denied for this connector; reauthorize and reprobe",
      httpStatus: null,
    };
  }
  // unverified: requested dispute intent allows a controlled provider probe only.
  const requested = opts.requestedScopes ?? opts.grantedScopes;
  if (ebayGrantedScopesInclude(requested, EBAY_PAYMENT_DISPUTE_SCOPE)) {
    return { poll: true, mode: "probe", reason: null };
  }
  return {
    poll: false,
    outcome: "blocked",
    errorClass: "AUTH_SCOPE",
    reason: "eBay payment dispute capability is unverified for this connector; no trusted provider evidence",
    httpStatus: null,
  };
}

/** Runtime planner: HTTP only when verified, or controlled probe when requested. */
export function planEbayPaymentDisputeFamilyPoll(opts: {
  capabilityStatus?: EbayPaymentDisputeCapabilityStatus | null;
  credentialGeneration?: string | null;
  requestedScopes?: unknown;
  /** @deprecated Use requestedScopes + capabilityStatus. */
  grantedScopes?: unknown;
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
}):
  | {
    action: "skip_http";
    outcome: "blocked";
    errorClass: "AUTH_SCOPE";
    reason: string;
    classified: ReturnType<typeof classifyInboundFamilyPoll>;
    createBuyerIssue: false;
  }
  | {
    action: "http";
    mode: "poll" | "probe";
    search: ReturnType<typeof planEbayPaymentDisputeSummary>;
    createBuyerIssue: false;
  } {
  const capability = ebayInboundFamilyPollCapability({
    family: "payment_dispute",
    capabilityStatus: opts.capabilityStatus,
    credentialGeneration: opts.credentialGeneration,
    requestedScopes: opts.requestedScopes ?? opts.grantedScopes,
  });
  if (capability.poll === false) {
    return {
      action: "skip_http",
      outcome: capability.outcome,
      errorClass: capability.errorClass,
      reason: capability.reason,
      classified: classifyInboundFamilyPoll({ httpStatus: 403, eventCount: 0 }),
      createBuyerIssue: false,
    };
  }
  return {
    action: "http",
    mode: capability.mode,
    search: planEbayPaymentDisputeSummary({
      boundExternalOrderId: opts.boundExternalOrderId,
      requestedExternalOrderId: opts.requestedExternalOrderId,
    }),
    createBuyerIssue: false,
  };
}

/** After-sales watch is independent of 72h recent-order ingest listing. */
export const EBAY_INBOUND_WATCH_DAYS = 45;
export const EBAY_INBOUND_WATCH_BATCH = 15;

export type EbayInboundWatchCandidate = {
  connectorId: string;
  externalOrderId: string;
  salesOrderId: string;
  orderDate?: string | null;
  lastCheckedAt?: string | null;
  openInbound?: boolean;
};

export function ebayInboundWatchOpenRequest(opts: {
  kind?: unknown;
  initiator?: unknown;
  requestState?: unknown;
  orderCancelState?: unknown;
}): boolean {
  const kind = asText(opts.kind) as EbayInboundRequestKind;
  if (!EBAY_INBOUND_REQUEST_KINDS.includes(kind) || kind === "payment_dispute") return false;
  return ebayInboundEventIsActionable({
    kind,
    initiator: opts.initiator,
    requestState: opts.requestState,
    orderCancelState: opts.orderCancelState,
  });
}

export function ebayInboundWatchEligible(opts: {
  orderDate?: unknown;
  nowMs?: number;
  openInbound?: boolean;
  windowDays?: number;
}): boolean {
  if (opts.openInbound) return true;
  const t = Date.parse(String(opts.orderDate ?? ""));
  if (!Number.isFinite(t)) return false;
  const days = Math.max(1, Math.trunc(opts.windowDays ?? EBAY_INBOUND_WATCH_DAYS));
  return (opts.nowMs ?? Date.now()) - t <= days * 86400000;
}

export function planEbayInboundAfterSalesWatch(opts: {
  candidates: EbayInboundWatchCandidate[];
  connectorId: unknown;
  nowMs?: number;
  batchSize?: number;
  windowDays?: number;
}): {
  eligible: EbayInboundWatchCandidate[];
  checked: EbayInboundWatchCandidate[];
  deferred: EbayInboundWatchCandidate[];
  oldestAgeHours: number | null;
  windowDays: number;
  batchSize: number;
  mutateProvider: false;
  mutateStock: false;
  createRefund: false;
} {
  const connector = asText(opts.connectorId);
  const now = opts.nowMs ?? Date.now();
  const windowDays = Math.max(1, Math.trunc(opts.windowDays ?? EBAY_INBOUND_WATCH_DAYS));
  const batchSize = Math.min(50, Math.max(1, Math.trunc(opts.batchSize ?? EBAY_INBOUND_WATCH_BATCH)));
  const eligible = opts.candidates.filter((row) => {
    if (asText(row.connectorId) !== connector) return false;
    if (!asText(row.externalOrderId) || !asText(row.salesOrderId)) return false;
    return ebayInboundWatchEligible({
      orderDate: row.orderDate,
      nowMs: now,
      openInbound: row.openInbound === true,
      windowDays,
    });
  }).sort((a, b) => {
    const aCheck = Date.parse(String(a.lastCheckedAt ?? "")) || 0;
    const bCheck = Date.parse(String(b.lastCheckedAt ?? "")) || 0;
    const aNull = a.lastCheckedAt ? 1 : 0;
    const bNull = b.lastCheckedAt ? 1 : 0;
    if (aNull !== bNull) return aNull - bNull;
    if (aCheck !== bCheck) return aCheck - bCheck;
    return asText(a.salesOrderId).localeCompare(asText(b.salesOrderId));
  });
  const checked = eligible.slice(0, batchSize);
  const deferred = eligible.slice(batchSize);
  let oldestAgeHours: number | null = null;
  for (const row of eligible) {
    const t = Date.parse(String(row.orderDate ?? ""));
    if (!Number.isFinite(t)) continue;
    const age = (now - t) / 3600000;
    if (oldestAgeHours == null || age > oldestAgeHours) oldestAgeHours = Math.floor(age);
  }
  return {
    eligible,
    checked,
    deferred,
    oldestAgeHours,
    windowDays,
    batchSize,
    mutateProvider: false,
    mutateStock: false,
    createRefund: false,
  };
}

export function inboundDiscoveryProviderWrites(): 0 {
  return 0;
}

export function extractEbayInboundReturns(body: unknown, boundOrderId: unknown): EbayInboundEventEvidence[] {
  const rec = asRecord(body) ?? {};
  const list = Array.isArray(rec.members) ? rec.members : Array.isArray(rec.returns) ? rec.returns : rec.returnId ? [rec] : [];
  const bound = asText(boundOrderId);
  const out: EbayInboundEventEvidence[] = [];
  for (const row of list) {
    const item = asRecord(row);
    if (!item) continue;
    const summary = asRecord(item.summary) ?? item;
    const creation = asRecord(summary.creationInfo) ?? asRecord(item.creationInfo) ?? {};
    const returnId = asText(item.returnId ?? summary.returnId);
    if (!returnId) continue;
    const orderId = asText(summary.orderId ?? item.orderId);
    if (orderId && bound && orderId !== bound) continue;
    const reason = asText(creation.reason ?? creation.reasonType ?? summary.reason ?? summary.reasonType);
    const reasonType = asText(creation.reasonType ?? summary.reasonType);
    out.push({
      providerRequestId: returnId,
      kind: ebayInboundKindFromReturn({ reason, reasonType }),
      legacyOrderId: orderId || bound,
      initiator: asText(summary.buyerLoginName ?? item.buyerLoginName) ? "BUYER" : asText(item.requestorType) || "BUYER",
      reason: reasonType || reason,
      requestState: asText(summary.status ?? summary.state ?? item.status ?? item.state),
      requestedAt: dateValue(creation.creationDate ?? summary.creationDate),
    });
  }
  return out;
}

export function extractEbayInboundInquiries(body: unknown, boundOrderId: unknown): EbayInboundEventEvidence[] {
  const rec = asRecord(body) ?? {};
  const list = Array.isArray(rec.members) ? rec.members : Array.isArray(rec.inquiries) ? rec.inquiries : rec.inquiryId ? [rec] : [];
  const bound = asText(boundOrderId);
  const out: EbayInboundEventEvidence[] = [];
  for (const row of list) {
    const item = asRecord(row);
    if (!item) continue;
    const inquiryId = asText(item.inquiryId);
    if (!inquiryId) continue;
    const orderId = asText(item.legacyOrderId ?? item.orderId);
    if (orderId && bound && orderId !== bound) continue;
    const reason = asText(item.inquiryType ?? item.claimType ?? item.reason) || "ITEM_NOT_RECEIVED";
    out.push({
      providerRequestId: inquiryId,
      kind: "buyer_inr",
      legacyOrderId: orderId || bound,
      initiator: asText(item.inquiryBy ?? item.requestorType) || "BUYER",
      reason,
      requestState: asText(item.inquiryStatus ?? item.status),
      requestedAt: dateValue(item.creationDate ?? item.inquiryDate),
    });
  }
  return out;
}

export function extractEbayInboundCases(body: unknown, boundOrderId: unknown): EbayInboundEventEvidence[] {
  const rec = asRecord(body) ?? {};
  const list = Array.isArray(rec.members) ? rec.members : Array.isArray(rec.cases) ? rec.cases : rec.caseId ? [rec] : [];
  const bound = asText(boundOrderId);
  const out: EbayInboundEventEvidence[] = [];
  for (const row of list) {
    const item = asRecord(row);
    if (!item) continue;
    const caseId = asText(item.caseId);
    if (!caseId) continue;
    const orderId = asText(item.legacyOrderId ?? item.orderId);
    if (orderId && bound && orderId !== bound) continue;
    const reason = asText(item.caseType ?? item.claimType ?? item.reason);
    const kind: EbayInboundRequestKind = ebayInboundReasonIsInr(reason)
      ? "buyer_inr"
      : ebayInboundReasonIsSnad(reason)
        ? "buyer_snad"
        : "ebay_case";
    out.push({
      providerRequestId: caseId,
      kind,
      legacyOrderId: orderId || bound,
      initiator: asText(item.claimant ?? item.requestorType) || "BUYER",
      reason,
      requestState: asText(item.caseStatus ?? item.status),
      requestedAt: dateValue(item.creationDate),
    });
  }
  return out;
}

export function extractEbayInboundPaymentDisputes(body: unknown, boundOrderId: unknown): EbayInboundEventEvidence[] {
  const rec = asRecord(body) ?? {};
  const list = Array.isArray(rec.paymentDisputeSummaries)
    ? rec.paymentDisputeSummaries
    : Array.isArray(rec.payment_disputes)
      ? rec.payment_disputes
      : rec.paymentDisputeId ? [rec] : [];
  const bound = asText(boundOrderId);
  const out: EbayInboundEventEvidence[] = [];
  for (const row of list) {
    const item = asRecord(row);
    if (!item) continue;
    const id = asText(item.paymentDisputeId);
    if (!id) continue;
    const orderId = asText(item.orderId ?? item.legacyOrderId);
    if (orderId && bound && orderId !== bound) continue;
    out.push({
      providerRequestId: id,
      kind: "payment_dispute",
      legacyOrderId: orderId || bound,
      initiator: asText(item.buyerUsername) ? "BUYER" : "PAYPAL",
      reason: asText(item.reason ?? item.paymentDisputeReason),
      requestState: asText(item.paymentDisputeStatus ?? item.status),
      requestedAt: dateValue(item.openDate ?? item.creationDate),
    });
  }
  return out;
}

export function planEbayInboundEventPersist(opts: {
  connectorId: unknown;
  otherConnectorId?: unknown;
  evidence: EbayInboundEventEvidence | null;
  orderCancelState?: unknown;
}): {
  persist: boolean;
  createIssue: boolean;
  resolveIssue: boolean;
  mutateProvider: false;
  mutateStock: false;
  createRefund: false;
  isolated: boolean;
  evidence: EbayInboundEventEvidence | null;
  spec: ReturnType<typeof ebayInboundIssueSpec> | null;
} {
  const isolated = asText(opts.otherConnectorId) === "" || asText(opts.connectorId) !== asText(opts.otherConnectorId);
  const evidence = opts.evidence?.providerRequestId ? opts.evidence : null;
  const createIssue = Boolean(evidence) && ebayInboundEventIsActionable({
    kind: evidence!.kind,
    initiator: evidence!.initiator,
    requestState: evidence!.requestState,
    orderCancelState: opts.orderCancelState,
  });
  return {
    persist: Boolean(evidence),
    createIssue,
    resolveIssue: Boolean(evidence) && !createIssue,
    mutateProvider: false,
    mutateStock: false,
    createRefund: false,
    isolated,
    evidence,
    spec: evidence ? ebayInboundIssueSpec(evidence.kind) : null,
  };
}

export function inboundAfterSalesUiRows(events: Array<{
  request_kind: string;
  provider_request_id: string;
  initiator?: string | null;
  reason?: string | null;
  request_state?: string | null;
  provider_order_cancel_state?: string | null;
}>): Array<{ kind: string; label: string; state: string; id: string; actionable: boolean }> {
  return events.map((row) => {
    const kind = row.request_kind as EbayInboundRequestKind;
    const spec = EBAY_INBOUND_REQUEST_KINDS.includes(kind) ? ebayInboundIssueSpec(kind) : { title: row.request_kind };
    const actionable = EBAY_INBOUND_REQUEST_KINDS.includes(kind)
      ? ebayInboundEventIsActionable({
        kind,
        initiator: row.initiator,
        requestState: row.request_state,
        orderCancelState: row.provider_order_cancel_state,
      })
      : false;
    return {
      kind: row.request_kind,
      label: spec.title,
      state: (row.request_state ?? "").trim() || "unknown",
      id: row.provider_request_id,
      actionable,
    };
  });
}
